import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';

import { brand, textures, LOGO_DIR } from '../theme';
import Img from './Img';
import { useCompany } from '../content/ContentProvider';

/**
 * The site footer.
 *
 * Note what is not here: a street address. This is a service business, the
 * owner does not want one published, and the service area is described instead.
 * See docs/instructions.md before adding anything address-shaped.
 */
export default function Footer() {
	const company = useCompany();
	const year = new Date().getFullYear();

	const heading = {
		fontSize: '0.78rem',
		fontWeight: 700,
		letterSpacing: '0.18em',
		textTransform: 'uppercase',
		color: brand.accent,
		mb: 2,
	};

	const link = {
		color: brand.textInvertMuted,
		textDecoration: 'none',
		fontSize: '0.95rem',
		lineHeight: 2,
		transition: 'color 200ms ease',
		'&:hover': { color: brand.accent },
	};

	return (
		<Box
			component="footer"
			sx={{
				position: 'relative',
				overflow: 'hidden',
				backgroundColor: brand.darkDeep,
				color: brand.textInvert,
				pt: 8,
				pb: 4,
			}}
		>
			{/* The diagonal gold crosshatch, faded in from the right — the same
			    texture the PHP theme draws behind its CTA band. */}
			<Box
				aria-hidden="true"
				sx={{
					position: 'absolute',
					inset: 0,
					opacity: 0.16,
					...textures.crosshatch(),
					maskImage: 'linear-gradient(to right, transparent, #000 45%, #000)',
					pointerEvents: 'none',
				}}
			/>

			<Container sx={{ position: 'relative' }}>
				<Box
					sx={{
						display: 'grid',
						gap: 5,
						gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: '1.4fr 1fr 1fr 1.2fr' },
						// The email address is one long unbreakable-looking word; this
						// lets its column shrink and the address wrap instead of the
						// track forcing the page wider than a phone.
						'& > *': { minWidth: 0 },
					}}
				>
					<Box>
						{/* The house mark alone on a white rounded card — the columns
						    beside it already say everything the wordmark would repeat.
						    The card keeps the charcoal artwork off the dark ground. */}
						<Box
							sx={{
								display: 'inline-block',
								backgroundColor: brand.white,
								borderRadius: '20px',
								p: 2.5,
								boxShadow: '0 10px 30px rgb(0 0 0 / 35%)',
							}}
						>
							<Img
								
								src={`${LOGO_DIR}/logo-con-trans.svg`}
								alt={`${company.name} logo`}
								width={451}
								height={422}
								loading="lazy"
								sx={{ height: '8rem', width: 'auto', display: 'block' }}
							/>
						</Box>
					</Box>

					<Box>
						<Typography sx={heading}>Pages</Typography>
						<Stack>
							{[
								['Home', '/'],
								['Services', '/services'],
								['Gallery', '/gallery'],
								['About', '/about'],
								['Contact', '/contact'],
							].map(([label, to]) => (
								<Box key={to} component={RouterLink} to={to} sx={link}>
									{label}
								</Box>
							))}
						</Stack>
					</Box>

					<Box>
						<Typography sx={heading}>Get in touch</Typography>
						<Stack>
							<Box component="a" href={company.phone_href} sx={{ ...link, color: brand.white, fontWeight: 600 }}>
								{company.phone}
							</Box>
							<Box component="a" href={`mailto:${company.email}`} sx={link}>
								{company.email}
							</Box>
							<Box
								component="a"
								href={company.google_review_url}
								target="_blank"
								rel="noopener noreferrer"
								sx={link}
							>
								Review us on Google
							</Box>
							<Box
								component="a"
								href={company.google_profile_url}
								target="_blank"
								rel="noopener noreferrer"
								sx={link}
							>
								See us on Google
							</Box>
						</Stack>

						<Typography sx={{ ...heading, mt: 4 }}>Hours</Typography>
						<Stack>
							{(company.hours ?? []).map((slot) => (
								<Box key={slot.label} sx={{ fontSize: '0.9rem', color: brand.textInvertMuted, lineHeight: 1.8 }}>
									<Box component="span" sx={{ color: brand.textInvert, fontWeight: 600 }}>
										{slot.label}:
									</Box>{' '}
									{slot.time}
								</Box>
							))}
						</Stack>
					</Box>

					<Box>
						<Typography sx={heading}>Where we work</Typography>
						<Typography sx={{ color: brand.textInvertMuted, fontSize: '0.95rem', mb: 1 }}>
							{company.city}, {company.region} and nearby:
						</Typography>
						<Typography sx={{ color: brand.textInvertMuted, fontSize: '0.9rem', lineHeight: 1.9 }}>
							{(company.nearby ?? []).join(' · ')}
						</Typography>
					</Box>
				</Box>

				<Divider sx={{ borderColor: brand.lineLight, my: 4 }} />

				<Stack
					direction={{ xs: 'column', md: 'row' }}
					spacing={2}
					sx={{ justifyContent: 'space-between', alignItems: { md: 'center' } }}
				>
					<Typography sx={{ fontSize: '0.85rem', color: brand.textInvertMuted }}>
						© {year} {company.legal_name} · All rights reserved
					</Typography>

					<Stack direction="row" spacing={3}>
						<Box component={RouterLink} to="/privacy-legal" sx={{ ...link, fontSize: '0.85rem' }}>
							Privacy &amp; Legal
						</Box>
						<Box
							component="a"
							href={company.developer_url}
							target="_blank"
							rel="noopener noreferrer"
							sx={{ ...link, fontSize: '0.85rem' }}
						>
							Site by {company.developer}
						</Box>
					</Stack>
				</Stack>
			</Container>
		</Box>
	);
}
